(() => {
  const SHOPIFY_FILE_PATTERNS = [/\/cdn\/shop\/files\//, /cdn\.shopify\.com\/s\/files\//];
  const UPLOADCARE_PATTERN = /https?:\/\/ucarecdn\.com\/[0-9a-f-]+\//i;
  const RESPONSIVE_WIDTHS = [320, 480, 640, 768, 960, 1200, 1440, 1680, 2000];

  function normalizeUrl(url) {
    if (!url) return '';
    if (url.startsWith('//')) return `${window.location.protocol}${url}`;
    if (url.startsWith('/')) return `${window.location.origin}${url}`;
    return url;
  }

  function isShopifyFileUrl(url) {
    const normalized = normalizeUrl(url);
    return SHOPIFY_FILE_PATTERNS.some((pattern) => pattern.test(normalized));
  }

  function isUploadcareUrl(url) {
    return UPLOADCARE_PATTERN.test(normalizeUrl(url));
  }

  function isResponsiveAssetUrl(url) {
    return isShopifyFileUrl(url) || isUploadcareUrl(url);
  }

  function hasWidthDescriptors(srcSet) {
    return /\s\d+w(?:\s*,|$)/.test(srcSet || '');
  }

  function stripWidthParam(url) {
    return url.replace(/([?&])width=\d+/g, '$1').replace(/[?&]$/, '');
  }

  function withShopifyWidth(url, width) {
    const cleanUrl = stripWidthParam(url);
    const separator = cleanUrl.includes('?') ? '&' : '?';
    return `${cleanUrl}${separator}width=${width}`;
  }

  function withUploadcareWidth(url, width) {
    const normalized = normalizeUrl(url);
    const match = normalized.match(/^(https?:\/\/ucarecdn\.com\/[0-9a-f-]+\/)(.*?)(\?[^#]*)?(#.*)?$/i);
    if (!match || /\.svg(?:$|[?#/])/i.test(normalized)) return normalized;

    const [, base, rawRest = '', search = '', hash = ''] = match;
    let rest = rawRest
      .replace(/\/-\/preview\/[^/]+\//gi, '/')
      .replace(/\/-\/resize\/[^/]+\//gi, '/')
      .replace(/\/-\/smart_resize\/[^/]+\//gi, '/')
      .replace(/\/{2,}/g, '/')
      .replace(/^\//, '');

    if (!/(^|\/)-\/format\/[^/]+\//i.test(rest)) {
      rest = rest ? `-/format/auto/${rest}` : '-/format/auto/';
    }

    return `${base}-/resize/${width}x/${rest}${search}${hash}`;
  }

  function withWidth(url, width) {
    if (isShopifyFileUrl(url)) return withShopifyWidth(url, width);
    if (isUploadcareUrl(url)) return withUploadcareWidth(url, width);
    return normalizeUrl(url);
  }

  function parseMediaMaxWidth(media) {
    if (!media) return null;
    const match = media.match(/max-width:\s*(\d+)px/i);
    return match ? Number(match[1]) : null;
  }

  function buildSrcSet(url, maxWidth) {
    const widths = RESPONSIVE_WIDTHS.filter((width) => width <= Math.max(640, maxWidth));
    return widths.map((width) => `${withWidth(url, width)} ${width}w`).join(', ');
  }

  function isBelowFold(element) {
    const rect = element.getBoundingClientRect();
    return rect.top > window.innerHeight * 1.1;
  }

  function getTargetWidth(img) {
    const rect = img.getBoundingClientRect();
    const cssWidth = rect.width || img.clientWidth || window.innerWidth || 320;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    return Math.max(320, Math.min(2000, Math.ceil(cssWidth * dpr)));
  }

  function isGemPagesElement(el) {
    if (!el) return false;
    // Homepage sections cr-home-* (data-crh) load their own images, like the GemPages ones they replaced.
    if (el.closest && el.closest('[data-crh]')) return true;
    if (el.classList && (
      el.classList.contains('gf_image') ||
      el.classList.contains('gf_lazyload') ||
      el.classList.contains('gp_force_load')
    )) return true;
    if (el.hasAttribute && (
      el.hasAttribute('isNotLazyload') ||
      el.hasAttribute('isnotlazyload')
    )) return true;
    if (el.closest && (
      el.closest('.gryffeditor') ||
      el.closest('[class*="gp-"]') ||
      el.closest('.gf_row') ||
      el.closest('gp-carousel')
    )) return true;
    return false;
  }

  // 2026-10-11: [data-cr-noopt] (the review carousels, the footer logo) sizes its own images. Rewriting them re-downloaded
  // each photo and picked 1200-1680px files for 300-530px cards.
  function isOptOut(el) {
    return !!(el && el.closest && el.closest('[data-cr-noopt]'));
  }

  function optimizeSource(source) {
    if (isGemPagesElement(source) || isOptOut(source)) return;

    const rawUrl =
      source.getAttribute('data-srcset') ||
      source.getAttribute('data-srcSet') ||
      source.getAttribute('srcset');

    if (!isResponsiveAssetUrl(rawUrl)) return;

    const maxWidth = parseMediaMaxWidth(source.getAttribute('media'));
    if (!maxWidth) return;

    const srcSet = buildSrcSet(rawUrl, Math.min(2000, maxWidth * 2));
    source.setAttribute('srcset', srcSet);

    if (source.hasAttribute('data-srcset')) {
      source.setAttribute('data-srcset', srcSet);
    }

    if (source.hasAttribute('data-srcSet')) {
      source.setAttribute('data-srcSet', srcSet);
    }
  }

  function optimizeImage(img) {
    if (isGemPagesElement(img) || isOptOut(img)) return;

    const rawUrl = img.getAttribute('data-src') || img.getAttribute('src');
    if (!rawUrl) return;

    if (isBelowFold(img)) {
      img.setAttribute('loading', 'lazy');
      img.setAttribute('fetchpriority', 'low');
    }

    img.setAttribute('decoding', 'async');

    if (!isResponsiveAssetUrl(rawUrl)) return;

    const targetWidth = getTargetWidth(img);
    const srcSet = buildSrcSet(rawUrl, Math.max(1200, targetWidth));

    if (!hasWidthDescriptors(img.getAttribute('srcset'))) {
      img.setAttribute('srcset', srcSet);
    }

    if (!img.getAttribute('sizes')) {
      img.setAttribute('sizes', '100vw');
    }

    const optimizedUrl = withWidth(rawUrl, targetWidth);

    if (img.hasAttribute('src')) {
      img.setAttribute('src', optimizedUrl);
    }

    if (img.hasAttribute('data-src')) {
      img.setAttribute('data-src', optimizedUrl);
    }
  }

  function optimizeExternalLinks() {
    document.querySelectorAll('a[href]').forEach((link) => {
      if (
        !link.hostname ||
        link.hostname === window.location.hostname ||
        link.href.startsWith('mailto:') ||
        link.href.startsWith('tel:')
      ) {
        return;
      }

      const rel = link.getAttribute('rel') || '';
      if (!rel.includes('nofollow')) {
        link.setAttribute('rel', `${rel} noopener noreferrer nofollow`.trim());
      }
    });
  }

  function optimizePage() {
    document.querySelectorAll('picture source').forEach(optimizeSource);
    document.querySelectorAll('img').forEach(optimizeImage);
    optimizeExternalLinks();
  }

  document.addEventListener('DOMContentLoaded', optimizePage);
  window.addEventListener('load', optimizePage);
  setTimeout(optimizePage, 1200);
})();

